import * as React from 'react';
import { observableValue } from '@vscode/observables';
import { view, viewWithModel, ViewModel, prop } from '@vscode/observables-react';
import { styles, mergeStyles } from './styles.js';
import type { ExplorerTreeNode } from './types.js';

// ToggleSwitch with observable state
class ToggleSwitchModel extends ViewModel({}) {
  readonly checked = observableValue<boolean>(this, false);

  toggle = (): void => {
    this.checked.set(!this.checked.get(), undefined);
  };
}

const ToggleSwitch = viewWithModel(ToggleSwitchModel, {}, (reader, model) => {
  const checked = model.checked.read(reader);

  const trackStyle: React.CSSProperties = {
    position: 'relative',
    width: '36px',
    height: '18px',
    backgroundColor: checked ? 'var(--component-explorer-button-background)' : 'var(--component-explorer-input-background)',
    borderRadius: '9px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    border: '1px solid var(--component-explorer-input-border, transparent)',
  };

  const thumbStyle: React.CSSProperties = {
    position: 'absolute',
    top: '2px',
    left: checked ? '18px' : '2px',
    width: '12px',
    height: '12px',
    backgroundColor: 'var(--component-explorer-foreground)',
    borderRadius: '50%',
    transition: 'left 0.2s',
  };

  return (
    <div style={trackStyle} onClick={model.toggle}>
      <div style={thumbStyle} />
    </div>
  );
});

// PropertiesContent - shows mock properties for a component
const PropertiesContent = view(
  {
    node: prop.const<ExplorerTreeNode>(),
  },
  (_reader, props) => {
    if (props.node.type !== 'component') {
      return (
        <div style={{ padding: '12px', color: 'var(--component-explorer-descriptionForeground)', fontSize: '12px' }}>
          Select a component (not a folder) to view its properties
        </div>
      );
    }

    return (
      <div style={styles.propertiesPanel}>
        <div style={styles.propertyRow}>
          <div style={styles.propertyLabel}>
            children<span style={{ color: 'var(--component-explorer-descriptionForeground)' }}> : string</span>
          </div>
          <input
            type="text"
            style={styles.propertyInput as React.CSSProperties}
            defaultValue="Click me"
            placeholder="Enter text..."
          />
          <div style={styles.propertyDescription}>Button text content</div>
        </div>

        <div style={styles.propertyRow}>
          <div style={styles.propertyLabel}>
            variant<span style={{ color: 'var(--component-explorer-descriptionForeground)' }}> : select</span>
          </div>
          <select style={styles.propertySelect as React.CSSProperties} defaultValue="default">
            <option value="default">default</option>
            <option value="primary">primary</option>
            <option value="secondary">secondary</option>
            <option value="destructive">destructive</option>
          </select>
          <div style={styles.propertyDescription}>Visual style variant</div>
        </div>

        <div style={styles.propertyRow}>
          <div style={styles.propertyLabel}>
            size<span style={{ color: 'var(--component-explorer-descriptionForeground)' }}> : select</span>
          </div>
          <select style={styles.propertySelect as React.CSSProperties} defaultValue="default">
            <option value="sm">sm</option>
            <option value="default">default</option>
            <option value="lg">lg</option>
          </select>
          <div style={styles.propertyDescription}>Button size</div>
        </div>

        <div style={styles.propertyRow}>
          <div style={styles.propertyLabel}>
            disabled<span style={{ color: 'var(--component-explorer-descriptionForeground)' }}> : boolean</span>
          </div>
          <ToggleSwitch />
          <div style={styles.propertyDescription}>Disable the button</div>
        </div>
      </div>
    );
  }
);

// RightSidebar
export const RightSidebar = view(
  {
    selectedNode: prop.const<ExplorerTreeNode | undefined>(),
  },
  (_reader, props) => (
    <div style={mergeStyles(styles.sidebar, styles.sidebarRight)}>
      <div style={styles.sidebarHeader}>
        <span style={{ marginRight: '6px', color: 'var(--component-explorer-textLink-foreground)' }}>&lt;/&gt;</span>
        Props
      </div>
      <div style={styles.sidebarContent}>
        {props.selectedNode ? (
          <PropertiesContent node={props.selectedNode} />
        ) : (
          <div style={{ padding: '12px', color: 'var(--component-explorer-descriptionForeground)', fontSize: '12px' }}>
            Select a component to view its properties
          </div>
        )}
      </div>
    </div>
  )
);
